import { useRef } from 'react';
import { cx } from '../lib/cx';

export interface ChipOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  legend: string;
  hint?: string;
  error?: string;
  options: ChipOption[];
  value: string[];
  onChange: (next: string[]) => void;
  /** Once this many are chosen, the rest go quiet rather than disappearing. */
  max?: number;
}

/**
 * Large tappable chips. Selection is a green inset rule on a pale green ground,
 * the same accent the Smart Home site uses for its active states.
 */
export default function MultiSelect({
  legend,
  hint,
  error,
  options,
  value,
  onChange,
  max,
}: MultiSelectProps) {
  const atMax = max !== undefined && value.length >= max;

  /**
   * Toggles compose off a ref rather than off the `value` prop. Two taps inside
   * one render pass would otherwise both read the same stale array and the
   * second would overwrite the first — which is exactly what a double-tap on a
   * phone produces.
   */
  const latest = useRef(value);
  latest.current = value;

  const toggle = (v: string) => {
    const current = latest.current;
    let next: string[];
    if (current.includes(v)) next = current.filter((x) => x !== v);
    else if (max !== undefined && current.length >= max) return;
    else next = [...current, v];
    latest.current = next;
    onChange(next);
  };

  return (
    <fieldset className="flex flex-col gap-3 border-0 p-0">
      <legend className="t-h3 mb-1 text-ink">{legend}</legend>
      {hint && <p className="t-meta measure -mt-1 text-grey-600">{hint}</p>}

      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const selected = value.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={selected}
              disabled={!selected && atMax}
              onClick={() => toggle(o.value)}
              className={cx(
                'min-h-tap rounded-full border px-5 text-[0.95rem] transition-colors',
                'disabled:cursor-not-allowed disabled:opacity-45',
                selected
                  ? 'border-green-600 bg-green-050 font-semibold text-green-900 shadow-[inset_0_0_0_2px_var(--color-green-600)]'
                  : 'border-grey-300 bg-white text-ink hover:bg-grey-050',
              )}
            >
              {o.label}
            </button>
          );
        })}
      </div>

      {max !== undefined && (
        <p className="t-meta text-grey-600" aria-live="polite">
          <span className="t-numeral">{value.length}</span> of {max} chosen
        </p>
      )}
      {error && (
        <p className="t-meta font-semibold text-alert" role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
}
